import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { LoadingState } from 'core/common-entities/loading-state.enum';
import { QuoteDetails } from 'src/app/modules/shared/entities/quote-details.class';
import { StorageValues } from '../../../shared/entities/storage-values.enum';
import { AdvancedSearchRequestVars, AdvancedSearchService } from '../../services/advanced-search.service';
import { QuoteListResponse } from '../entities/quote-list.class';
import { SortDirection, SortOrders, SortQuotes } from '../entities/sort.enum';
import { QuoteDetailsService } from './quote-details.service';

@Injectable({
  providedIn: 'root'
})
export class QuoteListService {

  templateValues = {
    FederalPriorityCode: null,
    LeadStatus: null,
    LnStatus: null,
    QuoteStatus: null,
    QuoteType: null
  };
  isProcessing$ = new Subject<LoadingState>();
  quoteList$ = new Subject();
  orderList$ = new Subject();
  currentPage$ = new Subject<number>();
  totalOrderRecords$ = new Subject<number>();
  totalQuotesRecords$ = new Subject<number>();
  private quoteListResultCount = {
    exact: 0,
    partial: 0
  };
  private orderListResultCount = {
    exact: 0,
    partial: 0
  };

  pageSize = 15;
  private urls = {
    templateValues: '/api/quote/templatevalues',
    quoteList: '/api/quote/list',
    orderList: '/api/order/list',
    accountManagerByEmployeeId: '/api/accountManager/get'
  };
  isLoading;

  quoteListRequest = {
    url: this.urls.quoteList,
    payload: {
      date: {
        startDate: null,
        endDate: null,
      },
      nameFilter: {
        employeeId: '',
        partialSearch: false,
        bpNumber: '',
        customerName: '',
        quoteNumber: '',
        quoteName: '',
        dollarAmount: '',
        poNumber: '',
        partNumber: '',
        contact: ''
      },
      showForFilter: {
        quotesShowFor: 0
      },
      sort: {
        sortBy: 0,
        direction: 0
      },
      offset: 0,
      page: 0,
      isOrder: false
    }
  };

  orderListRequest = {
    url: this.urls.orderList,
    payload: {
      date: {
        startDate: null,
        endDate: null,
      },
      nameFilter: {
        employeeId: '',
        partialSearch: false,
        bpNumber: '',
        customerName: '',
        quoteNumber: '',
        quoteName: '',
        dollarAmount: '',
        poNumber: '',
        partNumber: '',
        contact: ''
      },
      showForFilter: {
        ordersShowFor: 0
      },
      sort: {
        sortBy: 0,
        direction: 0
      },
      offset: 0,
      page: 0,
      isOrder: true
    }
  };
  private pendingRequests = new Subscription();

  constructor(
    private apiService: ApiService,
    private appInfoService: AppInfoService,
    private quoteDetailsService: QuoteDetailsService,
    private advancedSearchService: AdvancedSearchService
  ) { }

  getTemplateValues(callback: (...args: any[]) => any, additionalUrls: string[] = []) {
    const cachedData = this.templateValues.FederalPriorityCode &&
      this.templateValues.LeadStatus &&
      this.templateValues.LnStatus &&
      this.templateValues.QuoteStatus &&
      this.templateValues.QuoteType;
    const observableList = [];

    if (cachedData) {
      observableList.push(
        of({
          FederalPriorityCode: this.templateValues.FederalPriorityCode,
          LeadStatus: this.templateValues.LeadStatus,
          LnStatus: this.templateValues.LnStatus,
          QuoteStatus: this.templateValues.QuoteStatus,
          QuoteType: this.templateValues.QuoteType
        })
      );
    } else {
      observableList.push(...[this.apiService.get(this.urls.templateValues)]);
    }

    additionalUrls.forEach(url => {
      observableList.push(this.apiService.get(url));
    });

    forkJoin(observableList).subscribe((resultArray) => {
      const results: any = resultArray[0];
      this.templateValues.FederalPriorityCode = results.FederalPriorityCode;
      this.templateValues.LeadStatus = results.LeadStatus;
      this.templateValues.LnStatus = results.LnStatus;
      this.templateValues.QuoteStatus = results.QuoteStatus;
      this.templateValues.QuoteType = results.QuoteType;

      callback(this.templateValues);
    });
  }

  private parseResultObj(object) {
    object.records = object.quotes || object.orders;
    return object;
  }

  private parseQuoteIsExpired(shouldCheck, parsedObj) {
    const result = parsedObj;
    if (shouldCheck && parsedObj.records && parsedObj.records.length) {
      result.records = this.addQuoteIsExpired(parsedObj.records);
    }
    return result;
  }

  private updateRecordList(offset, requestVars: AdvancedSearchRequestVars, resultObj, previousObjRecords,
    loadingState: LoadingState, countKey) {
    // only update the count on the first page of results. This prevents total page issues when the object is generated.
    if (offset === 0) {
      requestVars.resultCount[countKey] = resultObj.count;
    }

    requestVars.recordList$.next(previousObjRecords.concat(resultObj.records).slice(0, this.pageSize));
    requestVars.recordCount$.next(requestVars.resultCount.exact + requestVars.resultCount.partial);
    this.isProcessing$.next(loadingState);
  }

  private getResultsForRequest(requestObj, requestVars: AdvancedSearchRequestVars) {
    // cancel existing requests (if any), and reinitialize the subscription
    this.pendingRequests.unsubscribe();
    this.pendingRequests = new Subscription();

    const checkIfQuoteExpired = requestObj.url === this.urls.quoteList;
    const offsets = this.advancedSearchService.getOffsetsForPage(requestObj.payload, requestVars, this.pageSize);

    const requestObs = this.advancedSearchService.getRequestObservables<QuoteListResponse>(QuoteListResponse, requestObj, offsets);

    const exactCall = requestObs.exact.subscribe(exactResultObj => {
      let parsedExactObj = this.parseResultObj(exactResultObj);

      parsedExactObj = this.parseResultObj(this.parseQuoteIsExpired(checkIfQuoteExpired, parsedExactObj));
      this.updateRecordList(
        offsets.exactOffset,
        requestVars,
        parsedExactObj,
        [],
        LoadingState.partiallyLoaded,
        'exact'
      );

      const partialCall = requestObs.partial.subscribe((partialResultObj) => {
        let parsedPartialObj = this.parseResultObj(partialResultObj);
        parsedPartialObj = this.parseResultObj(this.parseQuoteIsExpired(checkIfQuoteExpired, parsedPartialObj));

        this.updateRecordList(
          offsets.partialOffset,
          requestVars,
          parsedPartialObj,
          parsedExactObj.records,
          LoadingState.fullyLoaded,
          'partial'
        );
      });
      this.pendingRequests.add(partialCall);
    });
    this.pendingRequests.add(exactCall);

  }

  setSessionStorageItems(key: string, value: any) {
    value = JSON.stringify(value);
    sessionStorage.setItem(key, value as string);
  }

  /**
   * Retrieves Quotes list
   * @param filterFields - The fields to use in filtering - as object
   * @param sort - The sort object: column, direction
   * @param shouldCache - True if we save sticky filters, otherwise false
   */
  getQuotesList(filterFields, sort?: Sort, shouldCache?: boolean) {
    if (filterFields) {
      const payload = this.mapParamsForFilteredResults(filterFields);
      payload.showForFilter = {
        quotesShowFor: filterFields.quotesShowFor
      };
      this.quoteListRequest.payload = payload;
      this.resetSortOnQuotes();
    }

    if (sort) {
      this.quoteListRequest.payload.sort = {
        sortBy: SortQuotes[sort.active],
        direction: SortDirection[sort.direction]
      };
      this.setSessionStorageItems(StorageValues.CachedSort + StorageValues.QuoteKey, sort);
    }
    this.updatePage(1, { forOrders: false });
    if (shouldCache && filterFields) {
      this.setSessionStorageItems(StorageValues.CachedFilters + StorageValues.QuoteKey, filterFields);
    }
  }

  getOrdersList(filterFields, sort?: Sort, shouldCache?: boolean) {
    if (filterFields) {
      const payload = this.mapParamsForFilteredResults(filterFields);
      payload.showForFilter = {
        ordersShowFor: filterFields.quotesShowFor
      };
      this.orderListRequest.payload = payload;
      this.resetSortOnOrders();
    }

    if (sort) {
      this.orderListRequest.payload.sort = {
        sortBy: SortOrders[sort.active],
        direction: SortDirection[sort.direction]
      };
      this.setSessionStorageItems(StorageValues.CachedSort + StorageValues.OrderKey, sort);
    }
    this.updatePage(1, { forOrders: true });
    if (shouldCache && filterFields) {
      this.setSessionStorageItems(StorageValues.CachedFilters + StorageValues.OrderKey, filterFields);
    }
  }

  private getRequestVarsForQuotes(): AdvancedSearchRequestVars {
    return {
      recordList$: this.quoteList$,
      recordCount$: this.totalQuotesRecords$,
      resultCount: this.quoteListResultCount
    };
  }

  private getRequestVarsForOrders(): AdvancedSearchRequestVars {
    return {
      recordList$: this.orderList$,
      recordCount$: this.totalOrderRecords$,
      resultCount: this.orderListResultCount
    };
  }

  updatePage(pageIndex, { forOrders }: any) {
    this.isProcessing$.next(LoadingState.loading);
    let requestObj;
    let requestVars: AdvancedSearchRequestVars;
    if (forOrders) {
      requestObj = this.orderListRequest;
      requestVars = this.getRequestVarsForOrders();
    } else {
      requestObj = this.quoteListRequest;
      requestVars = this.getRequestVarsForQuotes();
    }

    requestObj.payload.page = pageIndex;
    this.getResultsForRequest(requestObj, requestVars);
  }

  /*
   * The expiration field is calculated based on current time and the quote expiration time
   * the expiration time is based on quote type and is evaluated in the BE.
   * @param quoteResponse the quote response object
   */
  addQuoteIsExpired(quoteResponse: QuoteListResponse) {
    const now = new Date().getTime();
    if (quoteResponse.quotes && quoteResponse.quotes.length) {
      quoteResponse.quotes.forEach((quote: QuoteDetails) => {
        this.quoteDetailsService.checkQuoteIsExpired(quote, now);
      });
    }
    return quoteResponse;
  }

  private mapParamsForFilteredResults(value) {
    const param: any = {};
    param.status = 0;
    param.date = {
      startDate: value.startDate,
      endDate: value.endDate
    };
    param.nameFilter = {
      displayedSearchValue: value.displayedSearchValue,
      employeeId: value.accountManager?.employeeId,
      partialSearch: false,
      bpNumber: value.bpNumber,
      customerName: value.customerName,
      quoteName: value.quoteName,
      quoteNumber: value.quoteNumber,
      dollarAmount: value.dollarAmount?.toString(),
      poNumber: value.poNumber,
      partNumber: value.partNumber,
      contact: value.contact
    };

    return param;
  }

  resetSortOnQuotes() {
    sessionStorage.removeItem(StorageValues.CachedSort + StorageValues.QuoteKey);
    this.quoteListRequest.payload.sort = {
      sortBy: 0,
      direction: 0
    };
  }

  resetSortOnOrders() {
    sessionStorage.removeItem(StorageValues.CachedSort + StorageValues.OrderKey);
    this.orderListRequest.payload.sort = {
      sortBy: 0,
      direction: 0
    };
  }

  getAccountManagerInfo() {
    const employeeId = this.appInfoService.userData.UserInfo.EmployeeId;
    return this.apiService.get(`${this.urls.accountManagerByEmployeeId}/${employeeId}`);
  }
}
